import { ChangeEvent, useState } from 'react'
import Image from 'next/image'
import { ImageType } from '@/app/types/newsType'
import { Label } from './ui/label'
import { Button } from './ui/button'
import { useTranslation } from '@/app/i18n/client'

interface ImageUploaderProps {
  imageArray: ImageType[]
  setImage: (images: ImageType[]) => void
}

const NewsImageUploader = ({ imageArray, setImage }: ImageUploaderProps) => {
  const { t } = useTranslation()
  const [error, setError] = useState<string>('')
  const maxImages = 5

  const handleButtonClick = (): void => {
    document.getElementById('multiple-images')?.click()
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setError('')
    const files = event.target.files
    if (files) {
      const newImages: ImageType[] = []
      Array.from(files).forEach((file) => {
        if (!file.type.startsWith('image/')) {
          setError('Invalid file type.')
        } else if (file.size > 1 * 1000 * 1000) {
          setError('File size exceeds the limit of 1MB')
        } else {
          newImages.push({
            id: -1,
            url: URL.createObjectURL(file),
            redirect_url: '',
          })
        }
      })

      if (imageArray.length + newImages.length > maxImages) {
        setError(`Maximum ${maxImages} files allowed`)
      } else {
        setImage([...imageArray, ...newImages])
      }
    }
  }

  const handleRemovePhoto = (index: number): void => {
    const updatedImages = imageArray.filter((_, i) => i !== index)
    setImage(updatedImages)
  }

  const handleTextChange = (index: number, value: string): void => {
    const updatedImages = imageArray.map((image, i) =>
      i === index ? { ...image, redirect_url: value } : image
    )
    setImage(updatedImages)
  }

  return (
    <>
      <div className="flex w-full items-center gap-10 border-2 border-[#e0e0e0] rounded p-3.5 mobile:gap-3">
        <div
          className={`flex flex-col relative mobile:self-start items-center justify-center cursor-pointer ${imageArray?.length === maxImages && '!pointer-events-none	!cursor-not-allowed'}`}
        >
          <Label
            htmlFor="offer-images"
            className="relative h-[50px] w-[57px] shadow-[0_0_6px_0_rgba(0,0,0,0.18)] p-1 block"
          >
            <Image
              loading="lazy"
              className="h-[43px] w-[57px] object-contain"
              src="/assets/Icons/noImage/noImage1.svg"
              alt="Selected-images"
              width={100}
              height={100}
            />
          </Label>
          <span className="w-4 h-4 bg-primary absolute -top-2 -right-1 rounded-full flex justify-center items-center p-2.5 text-xs text-white">
            {imageArray?.length ?? 0}
          </span>
          <Button
            variant="smallBtn"
            className="text-xs mobile:p-[1]"
            onClick={handleButtonClick}
          >
            <Image
              loading="lazy"
              width={12}
              height={12}
              src="/assets/Icons/uploadIcon.svg"
              alt="upload-icon"
            />
            {t('Upload')}
          </Button>
        </div>

        <input
          type="file"
          id="multiple-images"
          className="hidden"
          onChange={handleFileChange}
          accept="image/png, image/jpeg, image/webp, image/jpg"
          multiple
        />
        <p className="font-10 text-gray_text font-bold text-center">
          {maxImages}{' '}
          {t(
            'Photos max, must be in PNG, JPG, JPEG, or WEBP format, with a total maximum size of 3 MB'
          )}
        </p>
      </div>

      <div className="flex flex-col gap-1 mt-2">
        <div className="grid grid-cols-2 mobile:grid-cols-1 gap-2">
          {imageArray.map((image, index) => (
            <div
              key={`img-index_${index}`}
              className="flex flex-row items-center gap-2 relative p-3 w-full"
            >
              {/* Image */}
              <div className="w-[50px] h-[50px] mobile:w-[47px] mobile:h-[47px] rounded-sm overflow-hidden bg-cover">
                <Image
                  loading="lazy"
                  alt="Uploaded Image"
                  className="object-cover w-full h-full"
                  width={50}
                  height={50}
                  src={image.url}
                />
              </div>

              {/* Text input */}
              <div className="flex-grow mx-3">
                <input
                  type="text"
                  className="w-full h-8 p-2 rounded-md border text-black hover:ring-1 "
                  placeholder="https://example.com"
                  value={image.redirect_url ?? ''}
                  onChange={(e) => handleTextChange(index, e.target.value)}
                />
              </div>

              {/* Cancel button */}
              <Image
                width={16}
                height={14.8}
                src="/assets/Icons/Delete.svg"
                alt="Delete"
                className="bg-[#fff] cursor-pointer hover:bg-[#fff]"
                onClick={() => handleRemovePhoto(index)}
              />
            </div>
          ))}
        </div>
      </div>

      <span className="text-[red] pl-2">{error}</span>
    </>
  )
}

export default NewsImageUploader
